// Shared text helpers: full date/time stamps and **bold** comment rendering.

export const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${date} · ${time}`;
};

const URL_REGEX = /(https?:\/\/[^\s<>"]+)/g;
const TRAILING_PUNCT = /[.,;!?)]+$/;
const MENTION_REGEX = /(@[A-Za-z][A-Za-z0-9\-_.']{0,30}(?:\s[A-Z][a-zA-Z0-9\-_.']{0,30})?)/g;
const BOLD_REGEX = /\*\*([^*\n]+?)\*\*/g;

const linkify = (text, key) =>
  text.split(URL_REGEX).map((bit, i) => {
    if (i % 2 === 0) return <span key={`${key}-t-${i}`}>{bit}</span>;
    let url = bit; let trail = "";
    const m = url.match(TRAILING_PUNCT);
    if (m) { trail = m[0]; url = url.slice(0, url.length - trail.length); }
    return (
      <span key={`${key}-u-${i}`}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="underline break-all hover:opacity-80"
          onClick={(e) => e.stopPropagation()}>{url}</a>{trail}
      </span>
    );
  });

const mentionsAndLinks = (text, key, mentions) => {
  if (!mentions) return linkify(text, key);
  return text.split(MENTION_REGEX).map((p, i) =>
    p.startsWith("@")
      ? <strong key={`${key}-m-${i}`} className="text-indigo-700">{p}</strong>
      : <span key={`${key}-c-${i}`}>{linkify(p, `${key}-${i}`)}</span>
  );
};

// Renders **bold**, @mentions and clickable links inside a comment body.
export const renderRichBody = (body, { mentions = true } = {}) => {
  if (!body) return null;
  return body.split(BOLD_REGEX).map((chunk, i) =>
    i % 2 === 1
      ? <strong key={`b-${i}`} className="font-bold" data-testid="rich-bold">{mentionsAndLinks(chunk, `b-${i}`, mentions)}</strong>
      : <span key={`n-${i}`}>{mentionsAndLinks(chunk, `n-${i}`, mentions)}</span>
  );
};

// Wraps (or unwraps) the textarea's current selection in ** markers.
export const toggleBold = (el, value) => {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  const before = value.slice(0, start);
  const sel = value.slice(start, end);
  const after = value.slice(end);
  if (before.endsWith("**") && after.startsWith("**")) {
    return { next: before.slice(0, -2) + sel + after.slice(2), selStart: start - 2, selEnd: end - 2 };
  }
  if (sel.startsWith("**") && sel.endsWith("**") && sel.length >= 4) {
    const inner = sel.slice(2, -2);
    return { next: before + inner + after, selStart: start, selEnd: start + inner.length };
  }
  return { next: `${before}**${sel}**${after}`, selStart: start + 2, selEnd: end + 2 };
};
